<template>
  <v-dialog
    :value="true"
    persistent
    max-width="520"
    overlay-color="#060f23"
    :overlay-opacity="0.82"
    content-class="gc-upload-dialog"
    @keydown.esc="cancel"
  >
    <div class="gc-upload-panel" role="dialog" aria-labelledby="gc-upload-title">
      <header class="gc-upload-panel__header">
        <v-icon class="gc-upload-panel__header-icon">mdi-cloud-upload-outline</v-icon>
        <h2 id="gc-upload-title" class="gc-upload-panel__title">Upload files</h2>
      </header>

      <div class="gc-upload-panel__meta">
        <div class="gc-upload-panel__meta-row">
          <span class="gc-upload-panel__meta-label">Upload to</span>
          <span class="gc-upload-panel__pill gc-upload-panel__pill--accent">{{ storage }}</span>
          <span class="gc-upload-panel__pill">{{ path }}</span>
        </div>
        <p
          v-if="maxUploadFilesCount || maxUploadFileSize"
          class="gc-upload-panel__limits"
        >
          <template v-if="maxUploadFilesCount">
            Max {{ maxUploadFilesCount }} file{{ maxUploadFilesCount === 1 ? '' : 's' }}
          </template>
          <template v-if="maxUploadFilesCount && maxUploadFileSize"> · </template>
          <template v-if="maxUploadFileSize">
            Max size {{ formatBytes(maxUploadFileSize) }}
          </template>
        </p>
      </div>

      <ul v-if="listItems.length" class="gc-upload-panel__files">
        <li
          v-for="(file, index) in listItems"
          :key="index"
          class="gc-upload-panel__file"
        >
          <div class="gc-upload-panel__file-thumb">
            <img v-if="file.preview" :src="file.preview" :alt="file.name">
            <v-icon v-else size="28">{{ icons[file.extension] || 'mdi-file-outline' }}</v-icon>
          </div>
          <div class="gc-upload-panel__file-info">
            <span class="gc-upload-panel__file-name">{{ file.name }}</span>
            <span class="gc-upload-panel__file-meta">
              {{ formatBytes(file.size) }} · {{ file.type || 'unknown' }}
            </span>
          </div>
          <button
            type="button"
            class="gc-upload-panel__file-remove"
            aria-label="Remove file"
            @click="remove(index)"
          >
            <v-icon small>mdi-close</v-icon>
          </button>
        </li>
      </ul>

      <div
        v-else
        class="gc-upload-panel__empty"
        role="button"
        tabindex="0"
        @click="$refs.inputUpload.click()"
        @keyup.enter="$refs.inputUpload.click()"
      >
        <v-icon size="40" class="gc-upload-panel__empty-icon">mdi-file-upload-outline</v-icon>
        <p class="gc-upload-panel__empty-text">Click to add files</p>
        <button type="button" class="gc-upload-panel__btn gc-upload-panel__btn--gold">
          <v-icon left small>mdi-plus-circle</v-icon>
          Choose files
        </button>
      </div>

      <footer class="gc-upload-panel__footer">
        <button type="button" class="gc-upload-panel__btn gc-upload-panel__btn--text" @click="cancel">
          Cancel
        </button>
        <div class="gc-upload-panel__footer-right">
          <button
            type="button"
            class="gc-upload-panel__btn gc-upload-panel__btn--outline"
            :disabled="!files.length"
            @click="clear"
          >
            <v-icon left small>mdi-close</v-icon>
            Clear
          </button>
          <button
            type="button"
            class="gc-upload-panel__btn gc-upload-panel__btn--outline"
            :disabled="maxUploadFilesCount && listItems.length >= maxUploadFilesCount"
            @click="$refs.inputUpload.click()"
          >
            <v-icon left small>mdi-plus-circle</v-icon>
            Add files
          </button>
          <button
            type="button"
            class="gc-upload-panel__btn gc-upload-panel__btn--gold"
            :disabled="!files.length"
            @click="upload"
          >
            Upload
            <v-icon right small>mdi-upload</v-icon>
          </button>
        </div>
      </footer>

      <input
        ref="inputUpload"
        type="file"
        multiple
        hidden
        @change="add"
      >

      <div v-if="uploading" class="gc-upload-panel__progress-layer">
        <p class="gc-upload-panel__progress-label">Uploading…</p>
        <div class="gc-upload-panel__progress-track">
          <div
            class="gc-upload-panel__progress-fill"
            :style="{ width: `${Math.ceil(progress)}%` }"
          />
        </div>
        <span class="gc-upload-panel__progress-pct">{{ Math.ceil(progress) }}%</span>
      </div>

      <v-progress-linear
        v-if="loading"
        indeterminate
        absolute
        top
        color="#f5c347"
        height="3"
      />
    </div>
  </v-dialog>
</template>

<script>
import { formatBytes } from '@/plugins/util';

const imageMimeTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export default {
  props: {
    path: String,
    storage: String,
    endpoint: Object,
    files: { type: Array, default: () => [] },
    icons: Object,
    axios: Function,
    maxUploadFilesCount: { type: Number, default: 0 },
    maxUploadFileSize: { type: Number, default: 0 }
  },

  data() {
    return {
      loading: false,
      uploading: false,
      progress: 0,
      listItems: []
    };
  },

  methods: {
    formatBytes,

    async filesMap(files) {
      const promises = Array.from(files).map(file => {
        const result = {
          name: file.name,
          type: file.type,
          size: file.size,
          extension: file.name.split('.').pop()
        };
        return new Promise(resolve => {
          if (!imageMimeTypes.includes(result.type)) {
            return resolve(result);
          }
          const reader = new FileReader();
          reader.onload = e => {
            result.preview = e.target.result;
            resolve(result);
          };
          reader.readAsDataURL(file);
        });
      });

      return Promise.all(promises);
    },

    async add(event) {
      const selected = Array.from(event.target.files);
      this.$emit('add-files', selected);
      this.$refs.inputUpload.value = '';
    },

    remove(index) {
      this.$emit('remove-file', index);
      this.listItems.splice(index, 1);
    },

    clear() {
      this.$emit('clear-files');
      this.listItems = [];
    },

    cancel() {
      this.$emit('cancel');
    },

    async upload() {
      const formData = new FormData();

      for (const file of this.files) {
        formData.append('files', file, file.name);
      }

      const url = this.endpoint.url
        .replace(new RegExp('{storage}', 'g'), this.storage)
        .replace(new RegExp('{path}', 'g'), this.path);

      try {
        const config = {
          url,
          method: this.endpoint.method || 'post',
          data: formData,
          onUploadProgress: progressEvent => {
            this.progress = (progressEvent.loaded / progressEvent.total) * 100;
          }
        };

        this.uploading = true;
        await this.axios.request(config);
        this.uploading = false;
        this.$emit('uploaded');
      } catch (e) {
        this.uploading = false;
        console.error(e);
      }
    }
  },

  watch: {
    files: {
      deep: true,
      immediate: true,
      async handler() {
        this.loading = true;
        this.listItems = await this.filesMap(this.files);
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/brand';

::v-deep .gc-upload-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible;
}

.gc-upload-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: min(88vh, 640px);
  background: linear-gradient(145deg, $gc-panel-dark 0%, $gc-panel-mid 100%);
  border: 1px solid rgba($gc-sky-blue, 0.3);
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  color: $gc-white;
}

.gc-upload-panel__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba($gc-sky-blue, 0.15);
  background: rgba($gc-navy, 0.45);
}

.gc-upload-panel__header-icon {
  color: $gc-gold !important;
}

.gc-upload-panel__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: $gc-white;
}

.gc-upload-panel__meta {
  padding: 14px 20px;
  border-bottom: 1px solid rgba($gc-sky-blue, 0.1);
}

.gc-upload-panel__meta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.gc-upload-panel__meta-label {
  font-size: 13px;
  color: rgba($gc-white, 0.5);
}

.gc-upload-panel__pill {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: rgba($gc-white, 0.9);
  background: rgba($gc-deep-blue, 0.6);
  border: 1px solid rgba($gc-sky-blue, 0.28);
  border-radius: 999px;
  word-break: break-all;

  &--accent {
    color: $gc-sky-blue;
    border-color: rgba($gc-sky-blue, 0.45);
    background: rgba($gc-sky-blue, 0.12);
  }
}

.gc-upload-panel__limits {
  margin: 8px 0 0;
  font-size: 12px;
  color: rgba($gc-white, 0.42);
}

.gc-upload-panel__files {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: 8px 0;
  list-style: none;
  overflow-y: auto;
  max-height: 280px;
}

.gc-upload-panel__file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border-bottom: 1px solid rgba($gc-sky-blue, 0.08);

  &:hover {
    background: rgba($gc-sky-blue, 0.06);
  }
}

.gc-upload-panel__file-thumb {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba($gc-navy, 0.5);
  border: 1px solid rgba($gc-sky-blue, 0.2);
  border-radius: 6px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .v-icon {
    color: rgba($gc-sky-blue, 0.8) !important;
  }
}

.gc-upload-panel__file-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gc-upload-panel__file-name {
  font-size: 14px;
  font-weight: 600;
  color: rgba($gc-white, 0.92);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-upload-panel__file-meta {
  font-size: 12px;
  color: rgba($gc-white, 0.45);
}

.gc-upload-panel__file-remove {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  .v-icon {
    color: rgba($gc-white, 0.55) !important;
  }

  &:hover {
    background: rgba(#ff6b6b, 0.15);

    .v-icon {
      color: #ff8a8a !important;
    }
  }
}

.gc-upload-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 16px 20px;
  padding: 32px 20px;
  text-align: center;
  border: 2px dashed rgba($gc-sky-blue, 0.28);
  border-radius: 8px;
  background: rgba($gc-navy, 0.35);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba($gc-gold, 0.5);
    background: rgba($gc-gold, 0.06);
  }
}

.gc-upload-panel__empty-icon {
  color: $gc-gold !important;
}

.gc-upload-panel__empty-text {
  margin: 0;
  font-size: 14px;
  color: rgba($gc-white, 0.55);
}

.gc-upload-panel__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid rgba($gc-sky-blue, 0.12);
  background: rgba($gc-panel-dark, 0.65);
}

.gc-upload-panel__footer-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.gc-upload-panel__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
  }

  &--text {
    color: rgba($gc-white, 0.7);
    background: transparent;

    &:hover:not(:disabled) {
      color: $gc-white;
      background: rgba($gc-white, 0.06);
    }
  }

  &--outline {
    color: rgba($gc-white, 0.88);
    background: transparent;
    border: 1px solid rgba($gc-sky-blue, 0.4);

    &:hover:not(:disabled) {
      border-color: rgba($gc-sky-blue, 0.65);
      background: rgba($gc-sky-blue, 0.1);
    }

    .v-icon {
      color: inherit !important;
    }
  }

  &--gold {
    @include gc-button-gold;
    padding: 8px 18px;

    .v-icon {
      color: $gc-navy !important;
    }
  }
}

.gc-upload-panel__progress-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: rgba($gc-panel-dark, 0.94);
}

.gc-upload-panel__progress-label {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: rgba($gc-white, 0.9);
}

.gc-upload-panel__progress-track {
  width: 100%;
  max-width: 320px;
  height: 22px;
  overflow: hidden;
  background: rgba($gc-sky-blue, 0.15);
  border-radius: 999px;
}

.gc-upload-panel__progress-fill {
  height: 100%;
  background: $gc-gradient-gold-horizontal;
  border-radius: 999px;
  transition: width 0.15s ease;
}

.gc-upload-panel__progress-pct {
  font-size: 14px;
  font-weight: 600;
  color: $gc-gold-light;
}
</style>
